/**
 * Canonical loan "stalled" business rule — SHARED SOURCE OF TRUTH.
 *
 * This is the Loan Center "stalled" flag definition, extracted so the Loan
 * Center (`activeLoanModel.deriveFlag`) and the Pipeline report's Stalled panel
 * (`activityReportModel.computeStalledLoans`) compute it identically instead of
 * drifting. `activeLoanModel.ts` re-exports these for its existing consumers.
 *
 * IMPORTANT — `isStalled()` means the LOAN CENTER STALLED-FLAG semantics, NOT
 * a generic "how long since any activity" recency check:
 *   - Basis is RESCUE-ONLY on `milestone_date`: a loan with no `milestone_date`
 *     is never stalled (conversation logs can only make a loan *less* stale,
 *     never newly stale). Mirrors the SQL `milestone_date IS NOT NULL` guard.
 *   - Lock state never suppresses activity monitoring. A loan may be stalled
 *     while its lock is active, expiring, or expired; lock timing belongs in
 *     the Lock column while this predicate drives the Flag column.
 *   - Threshold is a fixed `STALE_DAYS` (7) in Pacific calendar days.
 * If you want broad "last activity" recency (milestone OR conversation, no
 * milestone guard, clamped at 0), use `getDaysSinceLastActivity` instead.
 *
 * PARITY: the SQL predicate in `search_encompass_loans` is the server mirror of
 * this rule (see migration `20260610204522_stalled_flag_conversation_log_basis`
 * and `prospects/__tests__/segmentParity.test.ts`). Any change here must land in
 * the SQL too and keep the parity test green.
 *
 * Inputs are intentionally permissive (`string | null | undefined`) so both the
 * prospects `ProspectLoan` (optional `string`) and reports `PipelineLoan`
 * (`string | null`) satisfy them structurally — this service depends on neither
 * feature type.
 */
import { parseAsLocalDate } from '@/utils/dateHelpers';

/** Fixed thresholds (kept as named constants so client + SQL can't silently drift). */
export const STALE_DAYS = 7;
export const LOCK_EXPIRING_WINDOW_DAYS = 5;

export interface StaleBasisInput {
  milestone_date?: string | null;
  last_conversation_log_at?: string | null;
}

export interface LockInput {
  rate_lock_status?: string | null;
  rate_lock_buy_side_lock_expires?: string | null;
  shipping_investor_delivery_date?: string | null;
}

// Accept lock-shaped loan objects for caller compatibility, but intentionally
// ignore those fields when computing the activity-only stalled state.
export type StalledInput = StaleBasisInput & LockInput;

// ---------------------------------------------------------------------------
// Pacific business-date math (stale basis)
//
// The stalled flag and the SQL `p_segment` branch share ONE basis: calendar
// days in America/Los_Angeles. SQL computes
//   (GREATEST(milestone_date, last_conversation_log_at)
//      AT TIME ZONE 'America/Los_Angeles')::date
// and the client projects the same instants with Intl below. Raw values are
// parsed UTC-anchored (NOT via parseAsLocalDate) so a date-only string lands
// on the same Pacific date as a timestamptz at UTC midnight does in SQL.
// ---------------------------------------------------------------------------

const PT_DATE_FORMAT = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'America/Los_Angeles',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit'
});

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * Normalize Postgres/RPC timestamp text into a form Date.parse handles
 * consistently across browsers: space-separated timestamptz
 * ("2026-02-04 23:09:04+00") becomes T-separated, and a bare hour offset
 * ("+00" / "-08") gains minutes ("+00:00").
 */
function normalizeTimestamp(value: string): string {
  let normalized = value.trim();
  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}/.test(normalized)) {
    normalized = normalized.replace(' ', 'T');
  }
  // Pad a bare hour offset ("+00"/"-08" → "+00:00"/"-08:00") ONLY on values
  // with a time component — a date-only string ends in "-DD" and would be
  // corrupted by the same pattern.
  if (normalized.includes('T') && /[+-]\d{2}$/.test(normalized)) {
    normalized = `${normalized}:00`;
  }
  return normalized;
}

function parseInstant(value: string | Date | null | undefined): Date | null {
  if (!value) {
    return null;
  }
  const date = value instanceof Date ? value : new Date(normalizeTimestamp(value));
  return isNaN(date.getTime()) ? null : date;
}

/** Epoch-day index of an instant's Pacific calendar date. */
function ptEpochDay(date: Date): number {
  const [year, month, day] = PT_DATE_FORMAT.format(date).split('-').map(Number);
  return Date.UTC(year, month - 1, day) / MS_PER_DAY;
}

/** Whole Pacific calendar days from `earlier` to now. */
function ptCalendarDaysSince(earlier: Date): number {
  return ptEpochDay(new Date()) - ptEpochDay(earlier);
}

function differenceInCalendarDays(later: Date, earlier: Date): number {
  const a = new Date(later.getFullYear(), later.getMonth(), later.getDate()).getTime();
  const b = new Date(earlier.getFullYear(), earlier.getMonth(), earlier.getDate()).getTime();
  return Math.round((a - b) / MS_PER_DAY);
}

function toLocalMidnight(value: string | null | undefined): Date | null {
  if (!value) {
    return null;
  }
  const d = parseAsLocalDate(value);
  if (isNaN(d.getTime())) {
    return null;
  }
  d.setHours(0, 0, 0, 0);
  return d;
}

/**
 * Stale basis: the latest of `milestone_date` and `last_conversation_log_at`.
 * Rescue-only semantics — null when `milestone_date` is absent/unparseable:
 * conversation logs can make a loan less stale, never newly stale (mirrors
 * the SQL `milestone_date IS NOT NULL` guard).
 */
export function getStaleBasisDate(loan: StaleBasisInput): Date | null {
  const milestone = parseInstant(loan.milestone_date);
  if (!milestone) {
    return null;
  }
  const conversation = parseInstant(loan.last_conversation_log_at);
  return conversation && conversation.getTime() > milestone.getTime() ? conversation : milestone;
}

/** Pacific calendar days since the stale basis. Drives the stalled flag + badge count. */
export function getDaysStale(loan: StaleBasisInput): number | null {
  const basis = getStaleBasisDate(loan);
  return basis ? ptCalendarDaysSince(basis) : null;
}

/**
 * Display-only activity basis: latest of whichever activity dates exist
 * (no milestone guard — a milestone-less loan with conversation logs still
 * counts). Use for "last activity" recency; NEVER feeds the stalled flag.
 */
export function getLastActivityDate(loan: StaleBasisInput): Date | null {
  const candidates = [parseInstant(loan.milestone_date), parseInstant(loan.last_conversation_log_at)]
    .filter((date): date is Date => date !== null);
  if (candidates.length === 0) {
    return null;
  }
  return candidates.sort((a, b) => b.getTime() - a.getTime())[0];
}

/**
 * PT calendar days since getLastActivityDate. Clamped at 0 for future-dated
 * entries. Display-only recency (NOT the stalled flag).
 */
export function getDaysSinceLastActivity(loan: StaleBasisInput): number | null {
  const latest = getLastActivityDate(loan);
  return latest ? Math.max(0, ptCalendarDaysSince(latest)) : null;
}

// ---------------------------------------------------------------------------
// Lock state + days-until-expiration
// ---------------------------------------------------------------------------

export interface LockState {
  isCurrentlyLocked: boolean;
  isLockMet: boolean;
  isLockExpired: boolean;
  daysUntilExpiration: number | null;
}

export function deriveLockState(loan: LockInput): LockState {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const lockExpires = toLocalMidnight(loan.rate_lock_buy_side_lock_expires);
  const investorDelivery = toLocalMidnight(loan.shipping_investor_delivery_date);

  const isCurrentlyLocked = loan.rate_lock_status === 'Locked' && !!lockExpires && lockExpires >= today;
  const isLockMet = !!investorDelivery && !!lockExpires && investorDelivery <= lockExpires;
  const isLockExpired = !!lockExpires && lockExpires < today && !isLockMet;
  const daysUntilExpiration = lockExpires ? differenceInCalendarDays(lockExpires, today) : null;

  return { isCurrentlyLocked, isLockMet, isLockExpired, daysUntilExpiration };
}

/**
 * Canonical "stalled" predicate = the Loan Center stalled-flag semantics.
 * A loan is stalled when `getDaysStale >= STALE_DAYS` (rescue-only, so null →
 * not stalled). Lock state is deliberately outside this activity-only rule.
 */
export function isStalled(loan: StalledInput): boolean {
  const days = getDaysStale(loan);
  return days !== null && days >= STALE_DAYS;
}
